/**
 * THE RISK REGISTER (RAID) — first-class register, the pure layer.
 *
 * FlightDeck had no first-class risk object: a risk lived only as a row in a
 * rich-text table inside the charter (src/charter.ts). This makes the register the
 * SINGLE SOURCE OF TRUTH — the charter's "Risk register" section renders FROM it,
 * the portfolio and steering pack read it — and extends it to the full PMBOK/PRINCE2
 * RAID set: Risks, Assumptions, Issues and Dependencies, four sibling registers
 * under one home (a child of Plan).
 *
 * This module is the pure core — the four shapes, their taxonomies, and the
 * derivations the four screens read — so the register, the matrix, the charter
 * render and the portfolio can never disagree. No store, no database: the store
 * persists what the add/seed forms produce, and the tables (migration 0193) are the
 * record. Each item is PROJECT-SCOPED (a board you can open), modelled on
 * `decisions` / `milestones`.
 *
 * THE FOUR, AND WHAT EACH IS FOR (from the approved mockups):
 *  - RISK      — something that MIGHT happen. Severity = likelihood × impact (5×5),
 *                auto-derived and banded, so the register can't contradict itself.
 *                Grouped by severity. Has the matrix (heat-map) view.
 *  - ASSUMPTION— a premise we are relying on, and what breaks if it is wrong.
 *                A one-axis CONFIDENCE (low/med/high), never an L×I square, so an
 *                assumption never looks like a risk. Grouped by confidence (shakiest
 *                first). Invalidated → convert to a risk.
 *  - ISSUE     — a problem that has ALREADY happened. Reuses the app's own priority
 *                scale and marks (R23: ▲/↑/↓), never a new one. Grouped by priority.
 *                A risk that is realised becomes an issue (provenance chip).
 *  - DEPENDENCY— what this project needs from others (or others from it): a
 *                direction, a typed party, a needed-by, a four-stop status. Grouped
 *                by status (late first).
 */

import type { IssuePriority } from './types';
import { PRIORITY_ORDER } from './types';
import type { Lesson } from './lessons';
import { richText } from './rich-text';

/** The four registers. */
export type RaidKind = 'risk' | 'assumption' | 'issue' | 'dependency';
export const RAID_KINDS: RaidKind[] = ['risk', 'assumption', 'issue', 'dependency'];

/** The human reference prefix per register (R-01, A-01, I-01, D-01). */
export const RAID_REF_PREFIX: Record<RaidKind, string> = {
  risk: 'R', assumption: 'A', issue: 'I', dependency: 'D',
};

/** A register item's human reference, as every screen prints it. `num` is a
 *  per-board, per-register sequence the store assigns at create (like `position`),
 *  so the reference is stable and readable (R-01) rather than a raw uuid. */
export function raidRef(kind: RaidKind, num: number): string {
  return `${RAID_REF_PREFIX[kind]}-${String(num).padStart(2, '0')}`;
}

/** A Reveal deep-link target the item carries (the lesson it was seeded from, the
 *  risk an issue was realised from, a board a dependency points at). */
export interface RaidLink {
  kind: string;
  id: string;
  label: string;
}

// ──────────────────────────────────────────────────────────────────────────────
// RISKS
// ──────────────────────────────────────────────────────────────────────────────

export type RiskStatus = 'open' | 'mitigating' | 'monitoring' | 'closed';
export type RiskStrategy = 'reduce' | 'transfer' | 'accept' | 'avoid';
/** The 1–5 likelihood / impact scale; absent on an item logged "to assess". */
export type Score = 1 | 2 | 3 | 4 | 5;
export type SeverityBand = 'low' | 'medium' | 'high' | 'critical';

/** One risk as read back from the `risks` table (migration 0193). */
export interface Risk {
  id: string;
  boardId: string;
  num: number;
  /** The headline — what might happen. Leads the row. */
  title: string;
  /** The planned response (mitigation), plain text — rides under the title like the
   *  mockup's "Response:" line. Absent until one is written. */
  response?: string;
  /** Likelihood and impact, each 1–5. BOTH absent = "to assess" (no severity yet). */
  likelihood?: Score;
  impact?: Score;
  /** A free category (e.g. "Resourcing"); optional, for grouping/filtering later. */
  category?: string;
  strategy?: RiskStrategy;
  status: RiskStatus;
  owner?: string;
  /** When it was last reviewed (ms). */
  reviewedAt?: number;
  /** When the next review is due (ms); drives the "reviews due this week" count. */
  nextReviewAt?: number;
  /** The lesson this risk was seeded from (#490 learning loop); shows the "Seeded
   *  from a lesson" provenance chip. */
  seededFromLessonId?: string;
  links?: RaidLink[];
  madeBy: string;
  position?: number;
  createdAt: number;
  updatedAt?: number;
}

export const RISK_STATUS_LABELS: Record<RiskStatus, string> = {
  open: 'Open', mitigating: 'Mitigating', monitoring: 'Monitoring', closed: 'Closed',
};
export const RISK_STATUSES: RiskStatus[] = ['open', 'mitigating', 'monitoring', 'closed'];
export const RISK_STRATEGY_LABELS: Record<RiskStrategy, string> = {
  reduce: 'Reduce', transfer: 'Transfer', accept: 'Accept', avoid: 'Avoid',
};
export const RISK_STRATEGIES: RiskStrategy[] = ['reduce', 'transfer', 'accept', 'avoid'];

export const SEVERITY_BAND_LABELS: Record<SeverityBand, string> = {
  low: 'Low', medium: 'Medium', high: 'High', critical: 'Critical',
};
/** Severity bands, worst first — the order the register groups and the summary bar
 *  reads. */
export const SEVERITY_ORDER: SeverityBand[] = ['critical', 'high', 'medium', 'low'];

/** The 5×5 severity score = likelihood × impact. `undefined` when either axis is
 *  unassessed, so an un-scored risk reads "to assess" rather than a false Low. */
export function severityScore(likelihood?: number, impact?: number): number | undefined {
  if (!likelihood || !impact) return undefined;
  return likelihood * impact;
}

/** The band a score falls in (5×5 thresholds, matching the approved matrix):
 *  Low 1–4, Medium 5–9, High 10–14, Critical 15–25. One place, so the table square,
 *  the heat-map cell and the exposure bar can never band the same score differently. */
export function severityBand(score?: number): SeverityBand | undefined {
  if (score == null) return undefined;
  if (score >= 15) return 'critical';
  if (score >= 10) return 'high';
  if (score >= 5) return 'medium';
  return 'low';
}

/** A risk's band straight from its axes — the common path. */
export function riskBand(r: Pick<Risk, 'likelihood' | 'impact'>): SeverityBand | undefined {
  return severityBand(severityScore(r.likelihood, r.impact));
}

/** A risk still counts as open unless it is closed. */
export const isOpenRisk = (r: Pick<Risk, 'status'>): boolean => r.status !== 'closed';

// ──────────────────────────────────────────────────────────────────────────────
// ASSUMPTIONS
// ──────────────────────────────────────────────────────────────────────────────

/** One axis, never an L×I square — an assumption is not a risk. */
export type Confidence = 'low' | 'medium' | 'high';
export type AssumptionStatus = 'open' | 'validated' | 'invalidated';

export interface Assumption {
  id: string;
  boardId: string;
  num: number;
  /** The premise we are relying on. */
  title: string;
  /** What breaks if it proves false — rides under the title ("If it proves false:"). */
  impactIfFalse?: string;
  /** How sure we are. Absent until assessed. */
  confidence?: Confidence;
  owner?: string;
  status: AssumptionStatus;
  /** When it should be validated by (ms) — "Validate by 24 Sept". */
  validateByAt?: number;
  reviewedAt?: number;
  /** When invalidated and converted to a risk, the risk's id — shows "→ Converted
   *  to R-NN" (the convert loop). */
  convertedToRiskId?: string;
  links?: RaidLink[];
  madeBy: string;
  position?: number;
  createdAt: number;
  updatedAt?: number;
}

export const ASSUMPTION_STATUS_LABELS: Record<AssumptionStatus, string> = {
  open: 'Open', validated: 'Validated', invalidated: 'Invalidated',
};
export const ASSUMPTION_STATUSES: AssumptionStatus[] = ['open', 'validated', 'invalidated'];
export const CONFIDENCE_LABELS: Record<Confidence, string> = {
  low: 'Low', medium: 'Medium', high: 'High',
};
/** Shakiest first — the order the register groups and the summary bar reads. */
export const CONFIDENCE_ORDER: Confidence[] = ['low', 'medium', 'high'];
/** The confidence meter's filled stops out of three (low 1, medium 2, high 3). */
export const CONFIDENCE_FILL: Record<Confidence, number> = { low: 1, medium: 2, high: 3 };
export const CONFIDENCE_STOPS = 3;

/** An assumption is still open to validate unless it has been validated or invalidated. */
export const isOpenAssumption = (a: Pick<Assumption, 'status'>): boolean => a.status === 'open';
/** A low-confidence, still-open assumption is one to validate NOW — the summary's
 *  "N low confidence" count and the "Convert to risk" offer. */
export const isLowConfidenceOpen = (a: Pick<Assumption, 'status' | 'confidence'>): boolean =>
  a.status === 'open' && a.confidence === 'low';

// ──────────────────────────────────────────────────────────────────────────────
// ISSUES
// ──────────────────────────────────────────────────────────────────────────────

/** Reuses the app's own priority scale + marks (R23: ▲ critical / ↑ high / none
 *  medium / ↓ low) — never a new scale, so an issue reads with the same urgency
 *  language as a card. */
export type IssueStatus = 'open' | 'inprogress' | 'resolved';

export interface RegisterIssue {
  id: string;
  boardId: string;
  num: number;
  /** The problem that has already happened. */
  title: string;
  /** What is being done about it — rides under the title ("Next step:"). */
  nextStep?: string;
  priority: IssuePriority;
  owner?: string;
  status: IssueStatus;
  /** When it was raised (ms). */
  raisedAt?: number;
  /** When it was realised FROM a risk, the risk's id — shows "Realised from R-NN"
   *  (the convert loop; the action lives on the risk, the issue carries provenance). */
  realisedFromRiskId?: string;
  links?: RaidLink[];
  madeBy: string;
  position?: number;
  createdAt: number;
  updatedAt?: number;
}

export const ISSUE_STATUS_LABELS: Record<IssueStatus, string> = {
  open: 'Open', inprogress: 'In progress', resolved: 'Resolved',
};
export const ISSUE_STATUSES: IssueStatus[] = ['open', 'inprogress', 'resolved'];
/** Worst first — reuses the app's own priority order so the register sorts the same
 *  way a board does. */
export const ISSUE_PRIORITY_ORDER: IssuePriority[] = PRIORITY_ORDER;
export const isOpenIssue = (i: Pick<RegisterIssue, 'status'>): boolean => i.status !== 'resolved';

// ──────────────────────────────────────────────────────────────────────────────
// DEPENDENCIES
// ──────────────────────────────────────────────────────────────────────────────

/** Inbound = we depend on them; outbound = they depend on us. Shown as a glyph so
 *  the arrow points the dependency and you never read it the wrong way round. */
export type DepDirection = 'inbound' | 'outbound';
/** The other party, typed so "who owes this" reads at a glance. */
export type DepPartyKind = 'board' | 'person' | 'external';
/** Four-stop RAG, worst first. */
export type DepStatus = 'late' | 'atrisk' | 'ontrack' | 'met';

export interface Dependency {
  id: string;
  boardId: string;
  num: number;
  /** What is needed. */
  title: string;
  /** The detail — rides under the title. */
  note?: string;
  direction: DepDirection;
  partyKind: DepPartyKind;
  /** The other party's name (the board's name, the person's, the vendor's). */
  partyName: string;
  /** When the party is another board, its id — so the row can link straight to it (R15). */
  partyBoardId?: string;
  owner?: string;
  status: DepStatus;
  /** When it is needed by (ms); turns red when overdue and not met. */
  neededByAt?: number;
  links?: RaidLink[];
  madeBy: string;
  position?: number;
  createdAt: number;
  updatedAt?: number;
}

export const DEP_DIRECTION_LABELS: Record<DepDirection, string> = {
  inbound: 'We depend on them', outbound: 'They depend on us',
};
export const DEP_DIRECTION_SUBLABELS: Record<DepDirection, string> = {
  inbound: 'inbound · we need it', outbound: 'outbound · we supply it',
};
export const DEP_PARTY_LABELS: Record<DepPartyKind, string> = {
  board: 'another board', person: 'a person', external: 'external',
};
export const DEP_STATUS_LABELS: Record<DepStatus, string> = {
  late: 'Late', atrisk: 'At risk', ontrack: 'On track', met: 'Met',
};
/** Late first — the order the register groups and the summary bar reads. */
export const DEP_STATUS_ORDER: DepStatus[] = ['late', 'atrisk', 'ontrack', 'met'];
export const DEP_DIRECTIONS: DepDirection[] = ['inbound', 'outbound'];
export const DEP_PARTY_KINDS: DepPartyKind[] = ['board', 'person', 'external'];
export const DEP_STATUSES: DepStatus[] = ['late', 'atrisk', 'ontrack', 'met'];

/** A dependency is still open unless it has been met. */
export const isOpenDependency = (d: Pick<Dependency, 'status'>): boolean => d.status !== 'met';
/** Overdue — needed-by has passed and it is not met. Turns the needed-by date red.
 *  Compared as plain UTC days so a few hours of timezone never flips it (shared with
 *  handoff.ts's whole-day reading). */
export function depOverdue(d: Pick<Dependency, 'neededByAt' | 'status'>, nowMs: number): boolean {
  if (d.status === 'met' || d.neededByAt == null) return false;
  return utcDay(d.neededByAt) < utcDay(nowMs);
}
function utcDay(ms: number): number {
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

// ──────────────────────────────────────────────────────────────────────────────
// GROUPING — each register groups by its own worst-first band
// ──────────────────────────────────────────────────────────────────────────────

/** A grouped register: bands in worst-first order, each with its items, blanks
 *  dropped. The `key` is the band; `items` are already ordered within it. Generic so
 *  all four registers share one grouping shape. */
export interface RaidGroup<B extends string, T> {
  key: B;
  items: T[];
}

function groupBy<B extends string, T>(
  items: T[],
  order: B[],
  bandOf: (t: T) => B | undefined,
  fallback: B,
): RaidGroup<B, T>[] {
  const byBand = new Map<B, T[]>();
  for (const b of order) byBand.set(b, []);
  for (const it of items) {
    const b = bandOf(it) ?? fallback;
    (byBand.get(b) ?? byBand.set(b, []).get(b)!).push(it);
  }
  return order.map((key) => ({ key, items: byBand.get(key) ?? [] })).filter((g) => g.items.length > 0);
}

/** Risks grouped by severity, critical first. An unassessed risk ("to assess")
 *  falls to Low so it is never hidden. Within a band, highest score first, then by
 *  number. */
export function risksBySeverity(risks: Risk[]): RaidGroup<SeverityBand, Risk>[] {
  const ordered = [...risks].sort(
    (a, b) => (severityScore(b.likelihood, b.impact) ?? 0) - (severityScore(a.likelihood, a.impact) ?? 0) || a.num - b.num,
  );
  return groupBy(ordered, SEVERITY_ORDER, (r) => riskBand(r), 'low');
}

/** Assumptions grouped by confidence, shakiest first. Unassessed falls to Low (the
 *  ones to validate). Within a band, oldest-to-validate first, then by number. */
export function assumptionsByConfidence(items: Assumption[]): RaidGroup<Confidence, Assumption>[] {
  const ordered = [...items].sort(
    (a, b) => (a.validateByAt ?? Infinity) - (b.validateByAt ?? Infinity) || a.num - b.num,
  );
  return groupBy(ordered, CONFIDENCE_ORDER, (a) => a.confidence, 'low');
}

/** Issues grouped by priority, worst first. Within a band, oldest raised first. */
export function issuesByPriority(items: RegisterIssue[]): RaidGroup<IssuePriority, RegisterIssue>[] {
  const ordered = [...items].sort(
    (a, b) => (a.raisedAt ?? Infinity) - (b.raisedAt ?? Infinity) || a.num - b.num,
  );
  return groupBy(ordered, ISSUE_PRIORITY_ORDER, (i) => i.priority, 'low');
}

/** Dependencies grouped by status, late first. Within a band, soonest-needed first. */
export function dependenciesByStatus(items: Dependency[]): RaidGroup<DepStatus, Dependency>[] {
  const ordered = [...items].sort(
    (a, b) => (a.neededByAt ?? Infinity) - (b.neededByAt ?? Infinity) || a.num - b.num,
  );
  return groupBy(ordered, DEP_STATUS_ORDER, (d) => d.status, 'ontrack');
}

// ──────────────────────────────────────────────────────────────────────────────
// SUMMARY — the at-a-glance band each screen leads with (open count + distribution)
// ──────────────────────────────────────────────────────────────────────────────

const MS_WEEK = 7 * 24 * 60 * 60 * 1000;

export interface RiskSummary {
  open: number;
  /** Count per severity band (over ALL risks, as the exposure bar shows). */
  byBand: Record<SeverityBand, number>;
  critical: number;
  high: number;
  /** Open risks whose next review falls within the next 7 days. */
  reviewsDue: number;
  /** Elevated when any open risk is high or critical — drives the header chip. */
  elevated: boolean;
}
export function riskSummary(risks: Risk[], nowMs: number): RiskSummary {
  const byBand: Record<SeverityBand, number> = { critical: 0, high: 0, medium: 0, low: 0 };
  // An unassessed ("to assess") risk has no band, but it still exists: fold it into
  // Low — the same fallback risksBySeverity uses to group it — so the exposure bar's
  // segments always sum to the risk count and agree with the table's Low group. The
  // row itself still reads "to assess" (blank axes, no score square), so nothing
  // claims the risk IS low; it just isn't dropped from the picture.
  for (const r of risks) byBand[riskBand(r) ?? 'low']++;
  const openRisks = risks.filter(isOpenRisk);
  const critical = openRisks.filter((r) => riskBand(r) === 'critical').length;
  const high = openRisks.filter((r) => riskBand(r) === 'high').length;
  const reviewsDue = openRisks.filter(
    (r) => r.nextReviewAt != null && r.nextReviewAt - nowMs <= MS_WEEK,
  ).length;
  return { open: openRisks.length, byBand, critical, high, reviewsDue, elevated: critical + high > 0 };
}

// ──────────────────────────────────────────────────────────────────────────────
// REVIEWS — logging that a risk was looked at, and reading when the next is due
// ──────────────────────────────────────────────────────────────────────────────

/** The fallback interval between reviews — a month, the usual RAID cadence — used
 *  for a risk's FIRST review, when it was never on a rhythm to reuse. */
export const DEFAULT_REVIEW_INTERVAL_MS = 28 * 24 * 60 * 60 * 1000;

/** The next-review date to pre-fill when a risk is marked reviewed NOW. It reuses
 *  the rhythm the risk was already on — the gap between its last review and its
 *  next-due — so a fortnightly risk stays fortnightly and a monthly one monthly;
 *  with no prior rhythm it falls back to a month. Never shorter than a week, so a
 *  stale or inverted pair can't collapse the next review onto today. */
export function nextReviewDefault(risk: Pick<Risk, 'reviewedAt' | 'nextReviewAt'>, nowMs: number): number {
  const prior = risk.reviewedAt != null && risk.nextReviewAt != null
    ? risk.nextReviewAt - risk.reviewedAt
    : DEFAULT_REVIEW_INTERVAL_MS;
  return nowMs + Math.max(prior, MS_WEEK);
}

/** How a scheduled review reads against the clock: '' not due yet, 'due' within the
 *  next week (the window the summary counts), 'overdue' already past. */
export type ReviewDue = '' | 'due' | 'overdue';
export function reviewDue(nextReviewAt: number | undefined, nowMs: number): ReviewDue {
  if (nextReviewAt == null) return '';
  if (nextReviewAt < nowMs) return 'overdue';
  if (nextReviewAt - nowMs <= MS_WEEK) return 'due';
  return '';
}

export interface AssumptionSummary {
  open: number;
  byBand: Record<Confidence, number>;
  lowConfidence: number;
  invalidated: number;
}
export function assumptionSummary(items: Assumption[]): AssumptionSummary {
  const byBand: Record<Confidence, number> = { low: 0, medium: 0, high: 0 };
  for (const a of items) if (a.confidence) byBand[a.confidence]++;
  return {
    open: items.filter(isOpenAssumption).length,
    byBand,
    lowConfidence: items.filter(isLowConfidenceOpen).length,
    invalidated: items.filter((a) => a.status === 'invalidated').length,
  };
}

export interface IssueSummary {
  open: number;
  byBand: Record<IssuePriority, number>;
  critical: number;
  high: number;
  /** Issues resolved within the last 7 days — "N resolved this week". */
  resolvedThisWeek: number;
}
export function issueSummary(items: RegisterIssue[], nowMs: number): IssueSummary {
  const byBand: Record<IssuePriority, number> = { critical: 0, high: 0, medium: 0, low: 0 };
  for (const i of items) byBand[i.priority]++;
  const open = items.filter(isOpenIssue);
  return {
    open: open.length,
    byBand,
    critical: open.filter((i) => i.priority === 'critical').length,
    high: open.filter((i) => i.priority === 'high').length,
    resolvedThisWeek: items.filter(
      (i) => i.status === 'resolved' && i.updatedAt != null && nowMs - i.updatedAt <= MS_WEEK,
    ).length,
  };
}

export interface DependencySummary {
  open: number;
  byBand: Record<DepStatus, number>;
  late: number;
  atRisk: number;
  /** Open dependencies needed within the next 14 days. */
  dueSoon: number;
}
export function dependencySummary(items: Dependency[], nowMs: number): DependencySummary {
  const byBand: Record<DepStatus, number> = { late: 0, atrisk: 0, ontrack: 0, met: 0 };
  for (const d of items) byBand[d.status]++;
  const open = items.filter(isOpenDependency);
  return {
    open: open.length,
    byBand,
    late: items.filter((d) => d.status === 'late').length,
    atRisk: items.filter((d) => d.status === 'atrisk').length,
    dueSoon: open.filter((d) => d.neededByAt != null && d.neededByAt - nowMs <= 2 * MS_WEEK && d.neededByAt - nowMs >= 0).length,
  };
}

/** Per-tab live counts for the RAID tab strip. */
export interface RaidCounts {
  risk: number;
  assumption: number;
  issue: number;
  dependency: number;
}

// ──────────────────────────────────────────────────────────────────────────────
// THE MATRIX — a 5×5 likelihood × impact heat map (Risks only)
// ──────────────────────────────────────────────────────────────────────────────

export const LIKELIHOOD_LABELS: Record<Score, string> = {
  5: 'Almost certain', 4: 'Likely', 3: 'Possible', 2: 'Unlikely', 1: 'Rare',
};
export const IMPACT_LABELS: Record<Score, string> = {
  1: 'Negligible', 2: 'Minor', 3: 'Moderate', 4: 'Major', 5: 'Severe',
};
/** Rows top-to-bottom (likelihood 5→1) and columns left-to-right (impact 1→5). */
export const MATRIX_LIKELIHOODS: Score[] = [5, 4, 3, 2, 1];
export const MATRIX_IMPACTS: Score[] = [1, 2, 3, 4, 5];

/** The risks sitting in one matrix cell (a given likelihood × impact), by number. */
export function risksInCell(risks: Risk[], likelihood: Score, impact: Score): Risk[] {
  return risks
    .filter((r) => r.likelihood === likelihood && r.impact === impact)
    .sort((a, b) => a.num - b.num);
}

// ──────────────────────────────────────────────────────────────────────────────
// SEED FROM A LESSON — #490, the learning loop (lesson → risk only)
// ──────────────────────────────────────────────────────────────────────────────

/** Map a lesson's category to a risk category where it reads across, else leave it
 *  for the PM. Kept loose — category is free text on a risk. */
function categoryFromLesson(l: Pick<Lesson, 'category'>): string | undefined {
  const map: Record<string, string> = {
    scope: 'Scope', schedule: 'Schedule', cost: 'Cost', risk: 'Delivery',
    vendor: 'Vendor', quality: 'Quality', governance: 'Governance', team: 'Resourcing',
  };
  return map[l.category];
}

/** The pre-filled draft the seed dialog opens with — Risk = the lesson title,
 *  Planned response = the lesson's "next time" recommendation, Owner from the lesson,
 *  likelihood/impact left blank for the PM to assess. PREVIEW then CONFIRM: this only
 *  prefills; nothing is written until the dialog's "Add to register". */
export interface RiskDraft {
  boardId: string;
  title: string;
  response?: string;
  likelihood?: Score;
  impact?: Score;
  category?: string;
  strategy?: RiskStrategy;
  status: RiskStatus;
  owner?: string;
  nextReviewAt?: number;
  seededFromLessonId?: string;
  links?: RaidLink[];
}

export function riskDraftFromLesson(lesson: Lesson): RiskDraft {
  return {
    boardId: lesson.boardId ?? '',
    title: lesson.title,
    response: lesson.recommendation ? richText(lesson.recommendation).trim() || undefined : undefined,
    category: categoryFromLesson(lesson),
    strategy: 'reduce',
    status: 'open',
    owner: lesson.owner,
    seededFromLessonId: lesson.id,
  };
}

/** Whether a lesson has ALREADY seeded a risk in this register — the duplicate guard
 *  (#8: warn, never block). Returns the existing risk if so, for the "open R-NN"
 *  offer. */
export function riskSeededFrom(risks: Risk[], lessonId: string): Risk | undefined {
  return risks.find((r) => r.seededFromLessonId === lessonId);
}

// Drafts the add forms hand the store (num, madeBy, createdAt stamped by the store).
export interface AssumptionDraft {
  boardId: string;
  title: string;
  impactIfFalse?: string;
  confidence?: Confidence;
  owner?: string;
  status: AssumptionStatus;
  validateByAt?: number;
  links?: RaidLink[];
}
export interface IssueDraft {
  boardId: string;
  title: string;
  nextStep?: string;
  priority: IssuePriority;
  owner?: string;
  status: IssueStatus;
  raisedAt?: number;
  realisedFromRiskId?: string;
  links?: RaidLink[];
}
export interface DependencyDraft {
  boardId: string;
  title: string;
  note?: string;
  direction: DepDirection;
  partyKind: DepPartyKind;
  partyName: string;
  partyBoardId?: string;
  owner?: string;
  status: DepStatus;
  neededByAt?: number;
  links?: RaidLink[];
}

// ──────────────────────────────────────────────────────────────────────────────
// CHARTER RENDER — the charter's "Risk register" section reads FROM here
// ──────────────────────────────────────────────────────────────────────────────

/** A risk as the charter / portfolio / steering pack read it — the shape charter.ts
 *  already uses (CharterRisk), so the register becomes the single source of truth
 *  without the downstream reads changing. Open risks, worst first; closed dropped. */
export interface RiskForCharter {
  risk: string;
  likelihood: string;
  impact: string;
  /** The derived severity band word (Critical/High/Medium/Low), or '' when the risk
   *  is not yet assessed — the charter's Risk-register section shows it as a column. */
  severity: string;
  owner: string;
  mitigation: string;
}
export function risksForCharter(risks: Risk[], resolveName?: (who: string) => string): RiskForCharter[] {
  return risksBySeverity(risks.filter(isOpenRisk))
    .flatMap((g) => g.items)
    .map((r) => {
      const band = riskBand(r);
      // The owner is stored as an email; show the person's name when a resolver is
      // passed (the register table, the steering surfaces), the raw value otherwise.
      const owner = r.owner ? (resolveName ? resolveName(r.owner) : r.owner) : '';
      return {
        risk: r.title,
        likelihood: r.likelihood ? LIKELIHOOD_LABELS[r.likelihood] : '',
        impact: r.impact ? IMPACT_LABELS[r.impact] : '',
        severity: band ? SEVERITY_BAND_LABELS[band] : '',
        owner,
        mitigation: r.response ?? '',
      };
    });
}
